import type { CartLine } from '@telestore/contracts';
import { useSyncExternalStore } from 'react';

/**
 * Cart state.
 *
 * Deliberately client-side and per-shop: §6.3 says a customer must be able to
 * arrive from a Facebook ad and check out without signing into anything, so
 * there is no server-side cart to create an identity for.
 *
 * Persisted to localStorage because Cambodian mobile data drops connections
 * and a lost cart is a lost sale.
 */

export interface CartItem extends Omit<CartLine, 'modifiers'> {
  /** F&B modifier picks (§4); absent for retail and services. */
  modifiers?: Record<string, string[]>;
  /** Snapshot for display only; the server re-prices at quote and checkout. */
  name: string;
  imageUrl: string | null;
  unitPrice: number;
  options: string;
  slug: string;
}

interface CartState {
  shopSlug: string | null;
  items: CartItem[];
  /**
   * Codes the customer entered or arrived with. Held here rather than in a form
   * field so a `?promo=` link (§3 `promotion`) survives the walk from the
   * product page to checkout.
   */
  promotionCodes: string[];
}

const STORAGE_KEY = 'telestore.cart.v1';

const EMPTY: CartState = { shopSlug: null, items: [], promotionCodes: [] };

function load(): CartState {
  if (typeof localStorage === 'undefined') return EMPTY;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY;
    const parsed = JSON.parse(raw) as Partial<CartState>;
    if (!Array.isArray(parsed.items)) return EMPTY;
    return {
      shopSlug: parsed.shopSlug ?? null,
      items: parsed.items,
      // Carts written before v2 have no codes.
      promotionCodes: Array.isArray(parsed.promotionCodes) ? parsed.promotionCodes : [],
    };
  } catch {
    return EMPTY;
  }
}

let state: CartState = load();
const listeners = new Set<() => void>();

function emit() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Private browsing or a full quota — the cart still works for this session.
  }
  for (const listener of listeners) listener();
}

/**
 * A cart line's identity.
 *
 * The modifier picks are part of it (§4). Without them an iced coffee with
 * extra sugar and one without would collapse into a single line of two, and
 * the customer would get two of whichever was added first — the kind of error
 * a kitchen only discovers at the counter.
 *
 * Group ids and option ids are both sorted so the key depends on what was
 * chosen, never on the order it was tapped in.
 */
function keyOf(line: {
  productId: string;
  variantId: string | null;
  modifiers?: Record<string, string[]> | undefined;
}) {
  const picks = Object.entries(line.modifiers ?? {})
    .filter(([, options]) => options.length > 0)
    .map(([group, options]) => `${group}=${[...options].sort().join(',')}`)
    .sort()
    .join('|');
  return `${line.productId}:${line.variantId ?? ''}:${picks}`;
}

export const cart = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  snapshot(): CartState {
    return state;
  },

  /** Switching shops clears the cart — orders never span two merchants. */
  useShop(slug: string) {
    if (state.shopSlug !== slug) {
      // Codes go too: one shop's code is meaningless at another.
      state = { shopSlug: slug, items: [], promotionCodes: [] };
      emit();
    }
  },

  /**
   * Codes are stored uppercase and de-duplicated, so a customer who both taps a
   * `?promo=` link and types the same code by hand does not see it twice.
   */
  addPromotionCode(code: string) {
    const normalized = code.trim().toUpperCase();
    if (!normalized || state.promotionCodes.includes(normalized)) return;
    if (state.promotionCodes.length >= 5) return;
    state = { ...state, promotionCodes: [...state.promotionCodes, normalized] };
    emit();
  },

  removePromotionCode(code: string) {
    state = {
      ...state,
      promotionCodes: state.promotionCodes.filter((entry) => entry !== code),
    };
    emit();
  },

  add(item: CartItem) {
    const key = keyOf(item);
    const existing = state.items.find((line) => keyOf(line) === key);
    state = {
      ...state,
      items: existing
        ? state.items.map((line) =>
            keyOf(line) === key ? { ...line, quantity: line.quantity + item.quantity } : line,
          )
        : [...state.items, item],
    };
    emit();
  },

  setQuantity(key: string, quantity: number) {
    state = {
      ...state,
      items:
        quantity <= 0
          ? state.items.filter((line) => keyOf(line) !== key)
          : state.items.map((line) => (keyOf(line) === key ? { ...line, quantity } : line)),
    };
    emit();
  },

  remove(key: string) {
    state = { ...state, items: state.items.filter((line) => keyOf(line) !== key) };
    emit();
  },

  clear() {
    state = { ...state, items: [], promotionCodes: [] };
    emit();
  },

  lines(): CartLine[] {
    return state.items.map((item) => ({
      productId: item.productId,
      variantId: item.variantId,
      quantity: item.quantity,
      // §4 F&B: the picks travel with the line and are re-validated server-side.
      modifiers: item.modifiers ?? {},
    }));
  },
};

export function useCart(): CartState {
  return useSyncExternalStore(cart.subscribe, cart.snapshot, cart.snapshot);
}

export function cartItemKey(item: {
  productId: string;
  variantId: string | null;
  modifiers?: Record<string, string[]> | undefined;
}): string {
  return keyOf(item);
}

export function cartCount(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.quantity, 0);
}

export function cartSubtotal(items: CartItem[]): number {
  return items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
}
